// @givmo/board — every word the return's screens say in Board's own voice (CBG-YR5-DIST's web half,
// CBG-YR5-VIEW-11A and CBG-YR5-REVIEW; frames p4 to p7, the backend's
// `agent_docs/contracts/board-return-review.md` §5, §6 and §17, and the states no frame drew).
//
// Every line is approved: Jonathan approved them in copy round CBG-YR5-COPY1's next part (2026-10-16
// 11:54 PDT, "go with your recs", `scratchpad/2026-10-16-distweb-copy-round/copy-review.html`), as
// drawn but the review's, named neutrally (its §4). The marks say where a line came from:
//   * SAFE — the plan's registered safe phrase "Provided to every voting director on [date]" (§3.4);
//   * APPROVED — reused word for word from a shipped screen, each from its one definition, named;
//   * SERVED — shown as the backend serves it; the line here is only the fallback for a refusal that
//     arrives without a sentence of its own;
//   * DIST'S, APPROVED — the backend's own words for the copy, word for word, approved in copy
//     round CBG-YR5-COPY1's first part (Jonathan, 2026-10-16 07:38 PDT,
//     `scratchpad/2026-10-15-dist-copy-round/copy-review.html`).
//
// ⚠ NOTHING HERE ANSWERS LINE 11a OR 11b. No check, no "met", no "complete", no "ready", and nothing
// that says a review happened, was required or was enough: Board says what it recorded, and the
// board's own words (a motion, a consent's title) are quoted as theirs (`return-words.test.ts`).
// "Emailed" says Board handed the message to the mail service, never that anyone read it.
// A director is "they", never a gendered pronoun.

import { committeesWords } from '../committees/committees-words';
import { coiWords } from '../conflicts/coi-words';
import { consentDeliveryWords } from '../consents/consent-delivery-words';

/** "six", spelled out to ten as the frames write a count in a sentence; a numeral beyond. */
const SMALL = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

export function countWord(count: number): string {
  return SMALL[count] ?? String(count);
}

/** "Ruth Calloway", "Ruth Calloway and Theo Barrett", "A, B and C". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The first sentence of an approved two-sentence line: "The email didn’t go out." */
function firstSentence(line: string): string {
  const end = line.indexOf('. ');
  return end === -1 ? line : line.slice(0, end + 1);
}

/** "the email didn’t go out." — an approved sentence continued after a name and a colon. */
function continued(sentence: string): string {
  return sentence.charAt(0).toLowerCase() + sentence.slice(1);
}

export const returnWords = {
  // ── The Form 990 page (frame p4's header) ───────────────────────────────────────────────────────
  provideButton: 'Provide the return to the board',

  // ── Providing it (frame p4) ──────────────────────────────────────────────────────────────────────
  provideTitle: (year: number, form: string) => `Provide the ${year} Form ${form} to the board`,
  /** Not drawn: the same dialog, from an earlier copy's "Provide a newer copy". */
  provideNewerTitle: (year: number, form: string) =>
    `Provide a newer copy of the ${year} Form ${form}`,
  theReturnLabel: 'The return (PDF)',
  addFile: 'Add the PDF',
  replaceFile: 'Replace',
  /** While the file goes up and Board reads it. */
  addingFile: 'Adding the file…',
  fileFacts: (pages: number, size: string) =>
    `${pages} ${pages === 1 ? 'page' : 'pages'} · ${size}`,
  whoReceivesIt: 'Who receives it',
  yourDirectors: (names: readonly string[]) =>
    names.length === 1
      ? `Your director: ${names[0]}.`
      : `Your ${countWord(names.length)} directors: ${joinNames(names)}.`,
  /** "And Maren Lindqvist, Secretary and Treasurer, who isn’t a director." */
  notDirectors: (people: readonly string[]) =>
    people.length === 1
      ? `And ${people[0]}, who isn’t a director.`
      : `And ${joinNames(people)}, who aren’t directors.`,
  /** Not drawn: a roster with no director on it. */
  everyoneOnTheRoster: (people: readonly string[]) => `${joinNames(people)}.`,
  whatTheyGet: 'What they get',
  whatTheyGetBody: (year: number, form: string) =>
    `An email saying your ${year} Form ${form} is available to review in Givmo, with a link. Each person reads it after signing in to their own account, and Board records when each first opens it.`,
  keptNote:
    'Board shows the file exactly as you add it, and keeps this copy with your board’s records.',
  provideIt: 'Provide it',
  /** APPROVED (the shipped dialogs' Cancel). */
  cancel: 'Cancel',
  /** SERVED's own words (DIST's check), said before a file over the limit is sent at all. */
  tooLarge: 'This PDF is larger than 50 MB. Add a smaller copy of the return.',
  /** SERVED fallbacks: a file that couldn't be added, and an act that didn't go ahead. */
  fileRefused: 'We couldn’t add that file. Please try again.',
  provideFailed: 'We couldn’t provide the return just now. Please try again.',
  /** Not drawn: the recipients couldn't be read, so the dialog can't say who receives it. */
  recipientsFailed: 'We couldn’t load who receives it. Please try again.',

  // ── The receipts page (frame p7) ─────────────────────────────────────────────────────────────────
  receiptsTitle: (year: number, form: string) => `${year} Form ${form}, provided to the board`,
  /** SAFE (plan §3.4): every director recorded at the act was provided the copy on that day. */
  everyDirectorOn: (day: string) => `Provided to every voting director on ${day}.`,
  /** P2: every director was provided, the last of them on a later day (a printed copy, a later mail). */
  everyDirectorBy: (day: string) => `Provided to every voting director by ${day}.`,
  /** P2: some of the directors recorded at the act were provided the copy. */
  someDirectors: (day: string, provided: number, directors: number) =>
    `Provided on ${day} to ${provided} of the ${directors} directors.`,
  backToForm990: 'Form 990',

  // The email line (P4; contract §6). Every reason a mail didn't go reads the same (K-C1).
  eachEmailedOn: (day: string) => `Each was emailed on ${day}.`,
  eachEmailedExcept: (day: string) => `Each was emailed on ${day}, except:`,
  noOneEmailed: 'No one was emailed.',
  emailedLaterOn: (name: string, day: string) => `${name} was emailed on ${day}.`,
  /** APPROVED in part: "The email didn’t go out." (CBG-OPS1-CIRC-WEB, `consent-delivery-words.ts`). */
  emailDidntGo: (name: string) =>
    `${name}: ${continued(firstSentence(consentDeliveryWords.notSent))}`,
  /** APPROVED in part: "We can’t confirm the email went out." (CBG-OPS1-CIRC-WEB). */
  cantConfirm: (name: string) => `${name}: ${continued(consentDeliveryWords.notConfirmed)}`,

  people: 'People',
  colPerson: 'Person',
  colOpened: 'Opened',
  colRecordedReading: 'Recorded reading it',
  printedCopyGiven: (day: string) => `Printed copy given ${day}`,
  /** APPROVED (CBG-YR6-COPY1, `committees-words.ts`). */
  notADirector: committeesWords.notADirector,
  /** APPROVED (CBG-YR2-COPY1, `coi-words.ts`): said only while the seat is unjoined now. */
  hasntJoined: coiWords.hasntJoined,
  /** Not drawn: a seat no longer on the board's roster. */
  leftTheBoard: 'Left the board',
  /** A recorded reading on the paper road: the day the paper was signed. */
  onPaper: (day: string) => `${day}, on paper`,
  emailAgain: 'Email again',
  emailAgainSent: (name: string) => `${name} was emailed again.`,
  emailAgainFailed: 'We couldn’t email them just now. Please try again.',

  // The copy (frame p7's side card).
  theCopy: 'The copy',
  providedBy: (name: string) => `Provided by ${name}`,
  /** After the shipped director page's "Anything you record against this names this exact wording, by its fingerprint". */
  fingerprintLead:
    'Anything recorded against this copy names these exact bytes, by their fingerprint',
  openFileFailed: 'We couldn’t open the file just now. Please try again.',
  /** Not drawn: a copy an officer withdrew (contract states). */
  withdrawnOn: (day: string) => `Withdrawn on ${day}.`,
  /** SERVED's own words (DIST): a newer copy of the same return exists. */
  newerCopyProvided: 'A newer copy of this return was provided.',
  openNewerCopy: 'Open the newer copy',
  /** DIST'S, APPROVED: its stored body's line, said of a newer copy. */
  followsEarlierCopy: 'This copy follows an earlier copy of the same return.',
  openEarlierCopy: 'Open the earlier copy',

  // The quiet acts.
  recordPrintedCopy: 'Record a printed copy given',
  provideNewerCopy: 'Provide a newer copy',
  withdrawCopy: 'Withdraw this copy',

  // Record a printed copy given (a dialog).
  printedCopyTitle: 'Record a printed copy given',
  printedCopyPerson: 'Person',
  printedCopyPersonPlaceholder: 'Choose a person',
  printedCopyGivenOn: 'Given on',
  printedCopyNote: 'How it was given',
  printedCopyNotePlaceholder: 'For example, handed to them at a meeting',
  recordIt: 'Record it',
  printedCopyRecorded: (name: string) => `A printed copy given to ${name} is recorded.`,
  printedCopyFailed: 'We couldn’t record that just now. Please try again.',
  /** Not drawn: everyone sent the copy already has a printed copy recorded, or has left. */
  noOneForPrintedCopy: 'Everyone sent this copy already has a printed copy recorded.',

  // Withdraw this copy (a dialog).
  withdrawTitle: 'Withdraw this copy?',
  withdrawLead:
    'No one will be able to open it from Board after this. Its record, what was recorded of each person, and the file are kept.',
  withdrawReason: 'Why you’re withdrawing it',
  withdrawButton: 'Withdraw',
  withdrawn: 'The copy was withdrawn.',
  withdrawFailed: 'We couldn’t withdraw it just now. Please try again.',

  // The page's own answers.
  notFound: 'This copy isn’t available.',
  loadFailed: 'We couldn’t load this copy just now.',
  /** APPROVED (Evaluations, `assessment-words.ts`). */
  tryAgain: 'Try again',

  // ── The review (frame p7's second card) ──────────────────────────────────────────────────────────
  // Named neutrally, never "the board's" (the copy page's §4 choice): a committee's motion or consent
  // can be the one linked, and line 11b's instructions count a review by board committee members,
  // officers or management as well as by directors.
  theBoardsReview: 'The review',
  /** After the approved "No committees recorded" (CBG-YR6-COPY1). */
  noReviewRecorded: 'No review recorded',
  recordReview: 'Record the review',
  /** "Annual meeting · April 8, 2027": the meeting by its own title and day, never "board meeting". */
  meetingAndDay: (title: string, day: string) => `${title} · ${day}`,
  /** The tally, as the minutes record it. */
  tally: (outcome: string, votesFor: number, against: number, abstaining: number) =>
    `${outcome === 'failed' ? 'Failed' : 'Carried'} ${votesFor}–${against}${
      abstaining > 0 ? `, ${abstaining} abstaining` : ''
    }`,
  minutesApproved: (day: string) => `Minutes approved ${day}`,
  consentDated: (day: string) => `Written consent dated ${day}`,
  /** A consent recorded with no title of its own. */
  untitledConsent: 'Written consent',
  remove: 'Remove',
  /** Not drawn: the links removed from this copy, oldest first. */
  removedLink: (label: string, day: string) => `${label} — link removed ${day}`,
  removedLinkUnlabelled: (day: string) => `A link removed ${day}`,

  // Record the review (the picker).
  reviewPickerLead:
    'Choose a motion or written consent. Board links it to this copy as it was recorded.',
  motionsHeading: 'Motions with a recorded vote',
  consentsHeading: 'Written consents',
  /**
   * The list empty: what it holds, never what the board has — a closed session's voted motion and a
   * cancelled meeting's are never offered, so "Board has no motion with a recorded vote" can be untrue.
   */
  noCandidates:
    'There’s nothing to choose from yet. The list holds the motions with a recorded vote from an open session of a meeting that wasn’t cancelled, and the written consents waiting on signatures or signed by everyone.',
  candidatesFailed: 'We couldn’t load the motions and written consents just now.',
  reviewRecorded: 'The review is recorded for this copy.',
  reviewFailed: 'We couldn’t record that just now. Please try again.',

  // Remove the link (a dialog).
  removeTitle: 'Remove this link?',
  removeLead:
    'The motion or written consent stays as it was recorded. Board keeps a note that this link was removed.',
  removed: 'The link was removed.',
  removeFailed: 'We couldn’t remove it just now. Please try again.',

  // ── The copy's record, wherever the minute book shows it ─────────────────────────────────────────
  /** DIST'S, APPROVED: its kind label (the readable copy's heading, `KIND_LABEL`). */
  kindLabel: 'Copy of the Form 990',
  /**
   * DIST'S, APPROVED with its day: the line under the record (the backend's `approval_statement`);
   * nothing was approved. Without a day, this file's own forms.
   */
  recordProvidedBy: (name: string | null, day: string | null) =>
    name === null
      ? `Provided by someone whose name isn’t shown${day === null ? '' : `, on ${day}`}.`
      : `Provided by ${name}${day === null ? '' : ` on ${day}`}.`,
  /** The record's making event, in its history. */
  providedEvent: 'Provided to the board',

  // ── The director's home row (frame p5) and their copy page (frame p6) ────────────────────────────
  /** "2026 Form 990 · provided Apr 1": no badge — reading it is theirs to choose. */
  attentionRow: (year: number, form: string, day: string) =>
    `${year} Form ${form} · provided ${day}`,
  /** APPROVED (the director home's own button, `my-boards-page.tsx`). */
  open: 'Open',
  directorTitle: (year: number, form: string) => `${year} Form ${form}`,
  providedToTheBoard: (name: string | null, day: string) =>
    name === null
      ? `Provided to the board on ${day}.`
      : `Provided to the board by ${name} on ${day}.`,
  /**
   * APPROVED (the record page's way back, `director-records-page.tsx`: the officer record page's,
   * "Minute book", since CBG-UX10 named the director's book as the officer's).
   */
  minuteBook: 'Minute book',
  pdfPages: (pages: number) => `PDF · ${pages} ${pages === 1 ? 'page' : 'pages'}`,
  /** DIST'S, APPROVED: the mail's own button, on the page it leads to. */
  openTheReturn: 'Open the return',
  openedOn: (day: string, time: string) =>
    `Board recorded that you opened it on ${day}, at ${time}.`,
  ifYouveRead: 'If you’ve read it, you can record that here.',
  /** APPROVED (the shipped acknowledgment's button, D-CBG-18). */
  recordThatIveRead: 'Record that I’ve read this',
  /** APPROVED (the shipped acknowledgment's thanks). */
  thankYou: 'Thank you — it is recorded that you have read this.',
  /** SERVED's own words (DIST): the copy was withdrawn. */
  directorWithdrawn: 'This copy was withdrawn.',
  /** Not drawn: someone who wasn't on the board when the copy went out. Changed in CBG-FIX104 (the
   *  copy page's row 4.6, approved 2026-10-17 17:42 PDT): someone added to the board from a
   *  committee was on the roster then, on the committee. */
  notARecipient: 'You weren’t on the board when this copy was provided.',
  openFailed: 'We couldn’t open the return just now. Please try again.',
  /** APPROVED (the shipped record page's failed read). */
  directorLoadFailed: 'We couldn’t open this.',
  directorPageTitle: 'A copy of the return',

  // ── Line 11a and line 11b on the Form 990 page ───────────────────────────────────────────────────
  /** 11a's summary when the directors were provided the copy (frame p1: the short day, no stop). */
  line11aEveryOn: (day: string) => `Provided to every voting director on ${day}`,
  line11aEveryBy: (day: string) => `Provided to every voting director by ${day}`,
  line11aSome: (day: string, provided: number, directors: number) =>
    `Provided on ${day} to ${provided} of the ${directors} directors`,
  line11aNoDirectors: (day: string) => `Provided on ${day}`,
  line11aWithdrawn: (day: string) => `Provided ${day}, then withdrawn`,
  copiesHeading: 'Copies provided to the board',
  /** 11a's copy, its way to the receipts: the day and who provided it, in one line. */
  copyProvidedOn: (day: string, name: string | null = null) =>
    name === null ? `Provided ${day}` : `Provided ${day} by ${name}`,
  /** 11b's way to the copy a review was linked to. */
  ofTheCopyProvidedOn: (day: string) => `Of the copy provided ${day}`,
  receiptCounts: (opened: number, printed: number, recorded: number, people: number) =>
    [
      `${opened} of ${people} opened it`,
      printed > 0 ? `${printed} printed ${printed === 1 ? 'copy' : 'copies'} given` : null,
      `${recorded} recorded reading it`,
    ]
      .filter((part): part is string => part !== null)
      .join(' · '),
  copyWithdrawn: 'Withdrawn',
  copyFollowed: 'A newer copy followed',
  /**
   * 11b's summary: the act an officer linked as the review, named by the meeting's own title and
   * day (never "board meeting": a committee's meeting can be the one linked), or by the consent's
   * own title.
   */
  line11bMeeting: (title: string, day: string) => `The review: ${title} · ${day}`,
  line11bAct: (name: string) => `The review: ${name}`,
} as const;
